/**
 * KB-3319 — client wrapper for the `getCampDataEntityHistory` callable. Types mirror
 * `functions/src/camp-data/getEntityHistoryCore.ts`; the callable is the authority on `audience` and on
 * which fields a row carries (a director row is built from an allowlist server-side and simply lacks the
 * sensitive fields). The panel reads `result.audience`, never trusts a caller-supplied one.
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/services/firebaseService";

export type EntityKind = "offer" | "campLocation" | "campOrganization";
export type HistoryAudience = "staff" | "director";

export interface EntityHistoryInput {
  kind: EntityKind;
  id: string;
  field?: string;
  cursor?: string;
  limit?: number;
}

export interface HistoryItem {
  cid: string;
  fieldPath: string;
  fieldKey: string;
  outcomeLabel: string;
  when: string;
  hasValueRef: boolean;
  value?: unknown;
  restorable: boolean;
  evidence?: { pageUrl?: string } | null;
}

export interface HistoryBatch {
  when: string;
  sourceClass?: string; // staff only
  actorId?: string | null; // staff only
  resolvedName?: string | null; // staff only
  actorLabel?: string; // director only
  items: HistoryItem[];
}

export interface SnapshotRow {
  id: string;
  versionNumber: number | null;
  timestamp: string;
  user?: unknown; // staff only
  changeDescription?: string | null; // staff only
}

export interface EntityHistoryResult {
  audience: HistoryAudience;
  batches: HistoryBatch[];
  snapshots: SnapshotRow[] | null;
  nextCursor: string | null;
  legacyWinners: boolean;
}

export async function getEntityHistory(input: EntityHistoryInput): Promise<EntityHistoryResult> {
  const functions = getFunctions(app, "us-east4");
  const fn = httpsCallable<EntityHistoryInput, EntityHistoryResult>(functions, "getCampDataEntityHistory");
  const res = await fn(input);
  return res.data;
}
